import axios from "axios";
import { Token } from "../utils/localStorage.util";
import config from "../config.js";

const apiEmpresas = axios.create({
  baseURL: config.REACT_APP_HOST_API_EMPRESAS || "http://localhost:3335",
});

apiEmpresas.interceptors.request.use(
  async (config) => {
    const token = Token.getToken();
    if (token) {
      config.headers.Authorization = token;
    }

    if (!config.headers["Content-Type"] && !(config.data instanceof FormData)) {
      config.headers["Content-Type"] = "application/json";
    }

    return config;

  },
  (error) => {
    Promise.reject(error);
  }
);

apiEmpresas.interceptors.response.use(
  (response) => {
    return response.data;
  },

  async function (error) {
    if (error?.code === "ERR_NETWORK") {
      console.error("Erro de conexão (ERR_NETWORK):", error);
      return Promise.reject(error);
    }

    return Promise.reject(error);
  }
);

// Novos métodos para História 1
export function consultarCNPJ(cnpj) {
  // Ajuste para chamar o backend corretamente. O controller espera /consultar-cnpj/:cnpj
  // Garantir que cnpj venha limpo ou tratado
  const cnpjLimpo = cnpj.replace(/[^\d]/g, '');
  return apiEmpresas.get(`/empresas/consultar-cnpj/${cnpjLimpo}`);
}

// Consultar CEP usando API ViaCEP (gratuita, sem autenticação)
export async function consultarCEP(cep) {
  try {
    // Remove caracteres não numéricos
    const cepLimpo = cep.replace(/[^\d]/g, '');

    // Valida se tem 8 dígitos
    if (cepLimpo.length !== 8) {
      throw new Error('CEP inválido. Deve conter 8 dígitos.');
    }

    // Consulta API ViaCEP
    const response = await axios.get(`https://viacep.com.br/ws/${cepLimpo}/json/`);

    // Verifica se retornou erro (CEP não encontrado)
    if (response.data.erro) {
      throw new Error('CEP não encontrado.');
    }

    // Retorna dados formatados
    return {
      cep: response.data.cep,
      logradouro: response.data.logradouro,
      complemento: response.data.complemento,
      bairro: response.data.bairro,
      municipio: response.data.localidade,
      uf: response.data.uf,
      ibge: response.data.ibge
    };
  } catch (error) {
    if (error.message.includes('CEP')) {
      throw error;
    }
    throw new Error('Erro ao consultar CEP. Verifique sua conexão.');
  }
}


export function cadastrarEmpresa(dados) {
  return apiEmpresas.post(`/empresas`, dados);
}

export function atualizarEmpresa(id, dados) {
  return apiEmpresas.put(`/empresas/${id}`, dados);
}

export function atualizarEmpresaViaCNPJa(id) {
  return apiEmpresas.put(`/empresas/${id}/atualizar-via-cnpja`);
}

export function listarEmpresas(filters = {}) {
  // Se for objeto, converte para query string
  // Ex: { id_grupo_economico: 123 } -> ?id_grupo_economico=123
  const params = new URLSearchParams(filters).toString();
  return apiEmpresas.get(`/empresas?${params}`);
}

/** Busca dados completos da empresa (endereço, contato, grupo) para o modal de edição */
export function buscarDetalheEmpresa(idEmpresa) {
  return apiEmpresas.get(`/empresas/${idEmpresa}/detalhe`);
}

// Métodos Legados (Mantidos para compatibilidade)
export function obterFatorConversao(cnpj, textoBusca) {
  return apiEmpresas.get(`/fator-conversao/${cnpj}/${textoBusca}`);
}

export function cadastrarFatorConversao(body) {
  return apiEmpresas.post(`/fator-conversao`, body);
}

export async function atualizarFatorConversao(body) {
  return apiEmpresas.put(`fator-conversao/`, body);
}

export function cadastrarParceiroDetalhado(body) {
  return apiEmpresas.post(`/parceiros`, body);
}

export function alterarOrdemVinculosWorkflow(body) {
  return apiEmpresas.put(`/workflow/altera-ordem-vinculo`, body);
}

// Buscar usuários do sistema (para responsável comercial)
export async function buscarUsuarios() {
  try {
    const token = Token.getToken();
    const response = await axios.get(`${config.REACT_APP_HOST_API_AUTENTICACAO}/usuarios`, {
      params: { sistema: 'PTRIB' },
      headers: {
        'Authorization': token,
        'Content-Type': 'application/json'
      }
    });
    return response.data;
  } catch (error) {
    console.error('Erro ao buscar usuários:', error);
    throw error;
  }
}

// IDs dos grupos de usuários (estrutura existente)
export const ID_GRUPO_ASSOCIADO = 2;
export const ID_GRUPO_PARCEIRO = 3;
export const ID_GRUPO_RESPONSAVEL_COMERCIAL = 24;

// Buscar usuários filtrados por grupo (Associado = 2, Parceiro = 3)
// Utiliza a tabela public.usuarios na api-empresas (coluna id_grupo_usuario)
export async function buscarUsuariosPorGrupo(idGrupo) {
  try {
    const response = await apiEmpresas.get('/usuarios', {
      params: { id_grupo_usuario: idGrupo }
    });
    return response;
  } catch (error) {
    console.error('Erro ao buscar usuários por grupo:', error);
    throw error;
  }
}

// Buscar todos os grupos econômicos
export function listarGruposEconomicos(filters = {}) {
  const params = new URLSearchParams(filters).toString();
  return apiEmpresas.get(`/grupos-economicos?${params}`);
}

export function cadastrarGrupoEconomico(dados) {
  return apiEmpresas.post(`/grupos-economicos`, dados);
}

export function atualizarGrupoEconomico(id, dados) {
  return apiEmpresas.put(`/grupos-economicos/${id}`, dados);
}

export function vincularEmpresaAoGrupo(idGrupo, idEmpresa) {
  return apiEmpresas.post(`/grupos-economicos/${idGrupo}/vincular`, { idEmpresa });
}

export function desvincularEmpresaDoGrupo(idEmpresa) {
  return apiEmpresas.post(`/grupos-economicos/desvincular`, { idEmpresa });
}

export default apiEmpresas;
